'use client';

import { useSyncExternalStore } from 'react';

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'kontora-theme';
const CHANGE_EVENT = 'kontora-theme-change';

let temporaryTheme: Theme | null = null;
let subscribers = 0;

function readTheme(): Theme {
  if (temporaryTheme) return temporaryTheme;
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : 'system';
  } catch {
    return 'system';
  }
}

function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme !== 'system') return theme;
  return typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

function snapshot(): string {
  const theme = readTheme();
  return `${theme}:${resolveTheme(theme)}`;
}

function applyResolvedTheme(theme: ResolvedTheme): void {
  const root = document.documentElement;
  if (root.dataset.theme === theme) return;
  root.classList.add('k-theme-switching');
  void root.offsetHeight;
  root.dataset.theme = theme;
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => root.classList.remove('k-theme-switching'));
  });
}

function subscribe(listener: () => void): () => void {
  subscribers++;
  function update(event?: Event) {
    if (event instanceof StorageEvent) {
      if (event.key !== STORAGE_KEY && event.key !== null) return;
      temporaryTheme = null;
    }
    applyResolvedTheme(resolveTheme(readTheme()));
    listener();
  }
  const media =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-color-scheme: dark)')
      : null;
  window.addEventListener('storage', update);
  window.addEventListener(CHANGE_EVENT, update);
  media?.addEventListener('change', update);
  update();
  return () => {
    window.removeEventListener('storage', update);
    window.removeEventListener(CHANGE_EVENT, update);
    media?.removeEventListener('change', update);
    if (--subscribers === 0) temporaryTheme = null;
  };
}

function setTheme(theme: Theme): void {
  temporaryTheme = null;
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    temporaryTheme = theme;
  }
  applyResolvedTheme(resolveTheme(theme));
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

export function useTheme() {
  const value = useSyncExternalStore(subscribe, snapshot, () => 'system:light:pending');
  const [theme, resolvedTheme, pending] = value.split(':');
  return {
    theme: theme as Theme,
    resolvedTheme: resolvedTheme as ResolvedTheme,
    setTheme,
    isReady: !pending,
  };
}
